import React, { createContext, useContext, useEffect, useRef, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { TokenContext } from '@/Contexts/TokenContext';
import useApplication from '@/hooks/useApplication';
import useContact from '@/hooks/useContact';
import useDepartmentType from '@/hooks/useDepartmentType';
import usePartnerSubCategory from '@/hooks/usePartnerSubCategory';
import usePrograms from '@/hooks/usePrograms';
import useQuestions from '@/hooks/useQuestions';
import useStatesAndCounties from '@/hooks/useStatesAndCounties';
import useStatus from '@/hooks/useStatus';
import { ContactType } from '@/models/Contact';
import { CountryProps } from '@/models/Country';
import { CountyProps } from '@/models/County';
import { DepartmentTypeProps } from '@/models/DepartmentType';
import { EventTypeProps } from '@/models/EventType';
import { PartnerSubCategoryProps } from '@/models/PartnerSubCategory';
import { PartnerSubCategoryApiResponse, ProgramApiResponse } from '@/models/Program';
import { StandardProps } from '@/models/Standard';
import { StateProps } from '@/models/State';
import { StatusProps } from '@/models/Status';

import { LocaleContext } from './LocaleContext';
import { logError } from '@/utils/errorHandlerUtil';
import { Spinner } from '@/components/common/Spinner';
import { ApplicationDeadlineProps } from '@/models/ApplicationDeadline';
import { useApplicationDeadline } from '@/hooks/useApplicationDeadline';

interface Props {
  allPrograms: ProgramApiResponse[];
  contactTypes: ContactType[];
  deadlines: ApplicationDeadlineProps[];
  allStates: StateProps[];
  allCounties: CountyProps[];
  allCountries: CountryProps[];
  countrySelectOptions: { label: string; value: string }[];
  stateSelectOptions: { label: string; value: string }[];
  eventTypes: EventTypeProps[] | null;
  allProgramStandards: StandardProps[];
  allStatuses: StatusProps[];
  departmentTypes?: DepartmentTypeProps[];
  partnerSubCategories: PartnerSubCategoryProps[];
}

interface ProviderProps {
  children: React.ReactNode;
}

export const ConfigContext = createContext<Props>({
  allPrograms: [],
  contactTypes: [],
  deadlines: [],
  allStates: [],
  allCounties: [],
  allCountries: [],
  countrySelectOptions: [],
  stateSelectOptions: [],
  eventTypes: null,
  allProgramStandards: [],
  allStatuses: [],
  departmentTypes: [],
  partnerSubCategories: [],
});

export const ConfigProvider = ({ children }: ProviderProps) => {
  const navigate = useNavigate();
  const { authResult } = useContext(TokenContext);
  const { locale, localeLanguageCode } = useContext(LocaleContext);
  const { getContactTypes } = useContact();
  const { getAllPrograms } = usePrograms();
  const { getAllStatuses } = useStatus();
  const { getAllStates, getAllCounties, getAllCountries } = useStatesAndCounties();
  const { getEventTypes } = useQuestions();
  const { getAllProgramStandards } = useApplication();
  const { getDepartmentTypes } = useDepartmentType();
  const { getPartnerSubCategories } = usePartnerSubCategory();
  const { getAllApplicationDeadlines } = useApplicationDeadline();

  const [contactTypes, setContactTypes] = useState<ContactType[]>([]);
  const [deadlines, setDeadlines] = useState<ApplicationDeadlineProps[]>([]);
  const [allPrograms, setAllPrograms] = useState<ProgramApiResponse[]>([]);
  const [allStates, setAllStates] = useState<StateProps[]>([]);
  const [allCounties, setAllCounties] = useState<CountyProps[]>([]);
  const [allCountries, setAllCountries] = useState<CountryProps[]>([]);
  const [eventTypes, setEventTypes] = useState<EventTypeProps[] | null>(null);
  const [allProgramStandards, setAllProgramStandards] = useState<StandardProps[]>([]);
  const [allStatuses, setAllStatuses] = useState<StatusProps[]>([]);
  const [departmentTypes, setDepartmentTypes] = useState<DepartmentTypeProps[]>([]);
  const [partnerSubCategoryAPIResponse, setPartnerSubCategoryAPIResponse] = useState<
    PartnerSubCategoryApiResponse[]
  >([]);

  const [partnerSubCategories, setPartnerSubCategories] = useState<PartnerSubCategoryProps[]>([]);

  const hasFetchedCountriesRef = useRef(false);
  const hasFetchedProgramsRef = useRef(false);
  const hasFetchedContactTypesRef = useRef(false);
  const hasFetchedCountiesRef = useRef(false);
  const hasFetchedStatesRef = useRef(false);
  const hasFetchedDepartmentTypesRef = useRef(false);
  const hasFetchedPartnerSubCategoriesRef = useRef(false);
  const hasFetchedEventTypesRef = useRef(false);
  const hasFetchedProgramStandardsRef = useRef(false);
  const hasFetchedStatusesRef = useRef(false);
  const hasFetchedDeadlinesRef = useRef(false);

  const countrySelectOptionsToHide = useMemo(() => ['UU'], []);
  const stateSelectOptionsToHide = useMemo(() => ['IX'], []);

  const countrySelectOptions = useMemo(
    () =>
      allCountries
        .filter((country) => !countrySelectOptionsToHide.includes(country.abbrev))
        .sort((a: CountryProps, b: CountryProps) =>
          a.name > b.name ? 1 : b.name > a.name ? -1 : 0
        )
        .map((country) => {
          return {
            label: country.name,
            value: country.id.toString(),
          };
        }),
    [allCountries, countrySelectOptionsToHide]
  );
  const stateSelectOptions = useMemo(
    () =>
      allStates
        .filter((state) => !stateSelectOptionsToHide.includes(state.abbrev))
        .sort((a: StateProps, b: StateProps) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0))
        .map((state) => {
          return {
            label: state.abbrev,
            value: state.id,
          };
        }),
    [allStates, stateSelectOptionsToHide]
  );

  // TODO(bug): these fetchData() calls (programs, contact types, counties, countries, states,
  // department types, sub-categories, event types, standards) have no catch, and each hasFetched
  // ref is set *before* the await — so one transient failure of a core list (programs, states,
  // countries, contact types) is an unhandled rejection that is never retried, leaving the whole
  // app on the Spinner below forever. Expected: catch -> logError + retry or navigate('/error')
  // (as the statuses effect does), resetting the ref on failure.
  useEffect(() => {
    const fetchData = async () => {
      if (!hasFetchedProgramsRef.current && allPrograms.length === 0) {
        hasFetchedProgramsRef.current = true;
        const programs = await getAllPrograms();
        if (programs != null) setAllPrograms(programs);
      }
    };
    void fetchData();
  }, [allPrograms.length, getAllPrograms]);

  useEffect(() => {
    const fetchData = async () => {
      if (!hasFetchedContactTypesRef.current && contactTypes.length === 0) {
        hasFetchedContactTypesRef.current = true;
        const ct = await getContactTypes();
        if (ct != null) setContactTypes(ct);
      }
    };
    void fetchData();
  }, [contactTypes.length, getContactTypes]);

  useEffect(() => {
    const fetchData = async () => {
      if (!hasFetchedCountiesRef.current && allCounties.length === 0) {
        hasFetchedCountiesRef.current = true;
        const counties = await getAllCounties();
        if (counties != null) setAllCounties(counties);
      }
    };
    void fetchData();
  }, [allCounties.length, getAllCounties]);

  useEffect(() => {
    const fetchData = async () => {
      if (!hasFetchedCountriesRef.current && allCountries.length === 0) {
        hasFetchedCountriesRef.current = true;
        const countries = await getAllCountries();
        if (countries != null) {
          setAllCountries(countries);
        }
      }
    };

    void fetchData();
  }, [allCountries.length, authResult, getAllCountries]);

  useEffect(() => {
    const fetchData = async () => {
      if (!hasFetchedStatesRef.current && allStates.length === 0) {
        hasFetchedStatesRef.current = true;
        const states = await getAllStates();
        if (Array.isArray(states)) {
          setAllStates(states);
        }
      }
    };
    void fetchData();
  }, [allStates.length, authResult, getAllStates]);

  useEffect(() => {
    const fetchData = async () => {
      if (!hasFetchedDepartmentTypesRef.current && departmentTypes.length === 0) {
        hasFetchedDepartmentTypesRef.current = true;
        const departments = await getDepartmentTypes();
        if (Array.isArray(departments)) {
          setDepartmentTypes(departments);
        }
      }
    };
    void fetchData();
  }, [authResult, departmentTypes.length, getDepartmentTypes]);

  useEffect(() => {
    const fetchData = async () => {
      if (!hasFetchedPartnerSubCategoriesRef.current && partnerSubCategories.length === 0) {
        hasFetchedPartnerSubCategoriesRef.current = true;
        const subCategories = await getPartnerSubCategories();
        if (Array.isArray(subCategories)) {
          setPartnerSubCategoryAPIResponse(subCategories);
        }
      }
    };

    void fetchData();
  }, [getPartnerSubCategories, partnerSubCategories.length]);

  useEffect(() => {
    if (partnerSubCategoryAPIResponse.length === 0) return;

    const selectedLanguage = localeLanguageCode != null ? localeLanguageCode : 'en';

    const mappedSubCategories: PartnerSubCategoryProps[] = partnerSubCategoryAPIResponse
      .filter((subCategory) => typeof subCategory.id === 'number')
      .map((subCategory) => ({
        id: subCategory.id,
        subCategory:
          subCategory.subCategory[selectedLanguage] != null
            ? subCategory.subCategory[selectedLanguage]
            : '',
      }));

    setPartnerSubCategories(mappedSubCategories);
  }, [partnerSubCategoryAPIResponse, localeLanguageCode]);

  // TODO(bug): event types are localized server-side (?language=) but are fetched only once, with
  // whatever locale is active first; hasFetchedEventTypesRef blocks a refetch when the locale
  // changes (e.g. a TCW user switching language), so event type names stay in the old language.
  // Expected: refetch (or cache per locale) when `locale` changes.
  useEffect(() => {
    const fetchData = async () => {
      if (
        !hasFetchedEventTypesRef.current &&
        locale != null &&
        (eventTypes == null || eventTypes.length == 0)
      ) {
        hasFetchedEventTypesRef.current = true;
        const et = await getEventTypes(locale);
        setEventTypes(et);
      }
    };
    void fetchData();
  }, [authResult, locale, getEventTypes, eventTypes]);

  useEffect(() => {
    const fetchData = async () => {
      if (
        !hasFetchedProgramStandardsRef.current &&
        authResult.accessToken != null &&
        allProgramStandards?.length == 0
      ) {
        hasFetchedProgramStandardsRef.current = true;
        const standards = await getAllProgramStandards();
        setAllProgramStandards(standards);
      }
    };
    void fetchData();
  }, [allProgramStandards?.length, authResult, getAllProgramStandards]);

  useEffect(() => {
    if (!hasFetchedDeadlinesRef.current && deadlines?.length == 0) {
      const fetchDeadlines = async () => {
        hasFetchedDeadlinesRef.current = true;
        try {
          const fetchedDeadlines: ApplicationDeadlineProps[] = await getAllApplicationDeadlines();

          setDeadlines(fetchedDeadlines);
        } catch (error) {
          logError(`Deadline fetch failed.`, error);
        }
      };

      void fetchDeadlines();
    }
  }, [deadlines?.length, getAllApplicationDeadlines, navigate]);

  useEffect(() => {
    if (!hasFetchedStatusesRef.current && allStatuses?.length == 0) {
      hasFetchedStatusesRef.current = true;
      getAllStatuses()
        .then((statuses: StatusProps[]) => {
          setAllStatuses(statuses);
        })
        .catch((error) => {
          logError(`Status fetch failed.`, error);

          navigate('/error');
        });
    }
  }, [allStatuses?.length, getAllStatuses, navigate]);

  const loading =
    allPrograms.length === 0 ||
    allStates.length === 0 ||
    allCountries.length === 0 ||
    contactTypes.length === 0;

  return (
    <ConfigContext.Provider
      value={{
        allPrograms,
        contactTypes,
        deadlines,
        allStates,
        allCounties,
        allCountries,
        countrySelectOptions,
        stateSelectOptions,
        eventTypes,
        allProgramStandards,
        allStatuses,
        departmentTypes,
        partnerSubCategories,
      }}
    >
      {loading ? <Spinner /> : children}
    </ConfigContext.Provider>
  );
};
